/* eslint-disable eqeqeq, max-lines */
/**
 * PhotoLightBox, a component that shows a modal slideshow of photos.
 */

import React from 'react';
import getPhotoScaledUrl from '../../utils/gaia/getPhotoScaledUrl';
import {RegionType} from '../../utils/gaia/RegionType';
import trackAnalytics from '../../utils/gaia/trackAnalytics';

export default class PhotoLightBox extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            alertOpen: false,
            regionType: props.regionType,
            regionId: props.regionId,
            index: props.index || 0,
            isVisible: (typeof props.isVisible !== 'undefined') ? props.isVisible : true,
            isFullscreen: (typeof props.isFullscreen !== 'undefined') ? props.isFullscreen : false,
        };
    }

    preloadNextPhoto() {
        if (this.props.photos.length === 0) return;
        const nextIndex = (this.state.index + 1) % this.props.photos.length;
        const img = document.createElement('img');
        img.src = getPhotoScaledUrl(this.props.photos[nextIndex].id);
    }

    componentDidUpdate() {
        if (this.lightboxOverlay_) {
            // give focus to overlay so that keyboard shortcuts are listened to
            setTimeout(() => { this.lightboxOverlay_.focus(); }, 0);
        }
        this.preloadNextPhoto();
    }

    componentDidMount() {
        if (this.lightboxOverlay_) {
            // give focus to overlay so that keyboard shortcuts are listened to
            setTimeout(() => { this.lightboxOverlay_.focus(); }, 0);
        }
        this.preloadNextPhoto();
    }

    /**
     * Set visibility
     *
     * @param {boolean} visible
     * @public
     */
    setVisible(visible) {
        if (visible) {
            // give focus to overlay so that keyboard shortcuts are listened to
            this.lightboxOverlay_.focus();
        }

        this.setState({
            isVisible: visible
        });
    }


    /**
     * Toggle current fullscreen state
     *
     * @public
     */
    fullscreenToggle() {
        this.setState({
            isFullscreen: !this.state.isFullscreen
        });
    }


    /**
     * Sets the photo index to the next, wraps to the beginning
     *
     * @public
     */
    nextPhoto() {
        const nextIndex = (this.state.index + 1) % this.props.photos.length;
        trackAnalytics('Photo Next', {category: 'Hike', index: nextIndex});
        this.setState({index: nextIndex});
    }


    /**
     * Sets the photo index to the previous, wraps to the end
     *
     * @public
     */
    prevPhoto() {
        // eslint-disable-next-line max-len
        const prevIndex = (this.state.index - 1 + this.props.photos.length) % this.props.photos.length;
        trackAnalytics('Photo Previous', {category: 'Hike', index: prevIndex});
        this.setState({index: prevIndex});
    }


    /**
     * Listen for keyboard shortcuts to control lightbox
     *
     * @public
     */
    handleKeyPress(e) {

        const focusTag = e.target.tagName;

        if (focusTag == 'INPUT' || focusTag == 'SELECT' || focusTag == 'TEXTAREA') {
            return;
        }

        if (e.key == 'ArrowLeft') {
            this.prevPhoto();
        } else if (e.key == 'ArrowRight') {
            this.nextPhoto();
        } else if (e.key == 'Escape') {
            this.close();
        } else if (e.key == 'F') {
            this.fullscreen();
        }
    }

    close() {
        this.setVisible(false);
    }


    /**
     * Hitting a "Comment" button will focus on the create comment input. Exits fullscreen.
     *
     * @public
     */
    handleCommentActionClick(e) {
        e.preventDefault();
        if (this.state.isFullscreen) {
            this.setState({
                isFullscreen: false
            });
            setTimeout(() => { this.comments_.focus(); }, 0);
        } else {
            this.comments_.focus();
        }


    }


    /**
     * Calls the "like" callback
     *
     * @public
     */
    handleLikeActionClick(id) {
        const photoIsLiked = false;
        if (photoIsLiked) {
            this.unlikePhoto(id);
        } else {
            this.likePhoto(id);
        }
    }

    likePhoto(id) {
        const params = {photo_id: id};

        const data = {
            method: 'POST',
            contentType: 'application/json',
            body: JSON.stringify(params)
        };

        fetch('/api/user/profile/favorites/', data).then(
            // success
            () => {},
            // error
            response => {
                let message = 'An unknown error occurred while saving';
                if (response.status == 400) {
                    if (response.data.non_field_errors) {
                        message = response.data.non_field_errors[0];
                    }
                } else if (response.status == 403) {
                    // User isn't logged in
                    window.location = '/register/';
                    return;
                }
                console.error(message);
            }
        );
        trackAnalytics('Favorite', {category: 'Details', type: 'photo'});
    }

    unlikePhoto(favoriteId) {

        const data = {
            method: 'DELETE'
        };

        fetch('/api/user/profile/favorites/favorite_id?=' + favoriteId, data).then(
            // success
            () => {},
            // error
            response => {
                let message = 'An unknown error occurred while saving';
                if (response.status == 400) {
                    if (response.data.non_field_errors) {
                        message = response.data.non_field_errors[0];
                    }
                } else if (response.status == 403) {
                    // User isn't logged in
                    window.location = '/register/';
                    return;
                }
                console.error(message);
            }
        );
    }

    /**
     * Return current photo for viewing based on the current state index
     *
     * @public
     */
    getCurrentPhoto() {
        return this.props.photos[this.state.index];
    }

    logRouteClick(title, url) {
        trackAnalytics('Route from Slideshow', {
            category: 'Hike',
            title: title,
            url: url,
            index: this.state.index
        });
    }

    makeCoverPhoto(photo_id) {
        const params = {photo_id: photo_id};

        const data = {
            method: 'POST',
            contentType: 'application/json',
            body: JSON.stringify(params),
            credentials: 'same-origin'
        };
        // eslint-disable-next-line max-len
        fetch('/api/trip/update/' + this.state.regionType + '/' + this.state.regionId + '/', data).then(
            // success
            () => {
                this.setState({alertOpen: true});
                sleep(2000).then(() => {
                    this.setState({alertOpen: false});
                });
            },
            // error
            response => {
                let message = 'An unknown error occurred while saving';
                if (response.status == 400) {
                    if (response.data.non_field_errors) {
                        message = response.data.non_field_errors[0];
                    }
                } else if (response.status == 403) {
                    // User isn't logged in
                    window.location = '/register/';
                    return;
                }
                console.error(message);
            }
        );
    }

    adminSettingForPhoto(photo_id, setting_id) {
        const params = {admin_photo_setting: setting_id};

        const data = {
            method: 'POST',
            contentType: 'application/json',
            body: JSON.stringify(params),
            credentials: 'same-origin'
        };
        fetch('api/objects/photo/' + photo_id + '/admin-settings/', data).then(
            // success
            () => {},
            // error
            response => {
                let message = 'An unknown error occurred while saving';
                if (response.status == 400) {
                    if (response.data.non_field_errors) {
                        message = response.data.non_field_errors[0];
                    }
                } else if (response.status == 403) {
                    // User isn't logged in
                    window.location = '/register/';
                    return;
                }
                console.error(message);
            }
        );
    }

    render() {
        if (!this.state.isVisible) {
            return null;
        }

        // Set current photo as image
        const currentPhoto = this.getCurrentPhoto();
        const photoStyle = {
            backgroundImage: `url("${getPhotoScaledUrl(currentPhoto.id)}")`
        };

        // Human readable index
        const photoIndexStr = this.state.index + 1;
        const photoLengthStr = this.props.photos.length;

        //  Set title as given photobox title (for example, an "album" like a trip, location, etc)
        // or current photo's title
        const title = this.props.title || currentPhoto.title;

        let lightboxClass = 'lightbox';
        if (this.state.isFullscreen) {
            lightboxClass += ' lightbox-fullscreen';
        }

        let coverPhotoButton = null;
        // eslint-disable-next-line max-len
        if ((this.state.regionType == RegionType.LAND || this.state.regionType == RegionType.KNOWN_ROUTE) && this.props.isStaff) {
            coverPhotoButton = (
                <a className='ga-button ga-button-primary ga-button-winter lightbox-photo-button'
                    onClick={() => this.makeCoverPhoto(currentPhoto.id)}>
                    <i className="material-icons ibmid">photo</i>&nbsp;
                    <span className='ibmid'>Make Cover Photo</span>
                </a>
            );
        }

        let photoAdminSettings = null;
        if (this.props.isStaff) {
            photoAdminSettings = (
                <a className='ga-button ga-button-primary ga-button-winter'>Admin photo setting:
                    <select
                        className='ga-button ga-button-primary ga-button-winter'
                        onChange={e => this.adminSettingForPhoto(currentPhoto.id, e.target.value)}
                    >
                        <option value="0">No admin setting</option>
                        <option value="1">Completely remove</option>
                        <option value="2">Make visible</option>
                        <option value="3">Boost photo ranking</option>
                        <option value="4">De-boost photo ranking</option>
                    </select>
                </a>
            );
        }

        return (
            <div
                tabIndex="-1"
                ref={d => { this.lightboxOverlay_ = d; }}
                onKeyDown={e => this.handleKeyPress(e)}
                className='lightbox-overlay'
            >
                <div
                    className='lightbox-close'
                    onClick={e => this.close(e)}
                >
                    <i className="material-icons md-36 lightbox-close-button">close</i>
                </div>
                <div className={lightboxClass}>

                    <div className='lightbox-photo-container'>
                        <div className='lightbox-photo' style={photoStyle} />

                        <div className='lightbox-photo-title'>
                            <div className='lightbox-photo-title-text'>
                                {currentPhoto.route_title &&
                                <div>
                                    From route:&nbsp;
                                    <a
                                        onClick={()=>{
                                            this.logRouteClick(
                                                currentPhoto.route_title,
                                                currentPhoto.route_url);
                                        }}
                                        target={'_blank'}
                                        href={currentPhoto.route_url}>
                                        {currentPhoto.route_title}
                                    </a>
                                </div>}
                            </div>
                            {title && title.indexOf('My Photo') < 0 &&
                                <div className='lightbox-photo-title-text'>{title}</div>
                            }
                            {coverPhotoButton}&nbsp;
                            {this.state.alertOpen &&
                                <div
                                    className="alert alert-success"
                                    style={{position: 'absolute', zIndex: 999}}
                                >
                                    Cover photo set! Wait half an hour for the cache to clear.
                                </div>
                            }
                            {photoAdminSettings}
                            <div className='lightbox-photo-title-index'>
                                {photoIndexStr} of {photoLengthStr}
                            </div>
                        </div>
                        <div
                            className='lightbox-photo-rightarrow'
                            onClick={e => this.nextPhoto(e)}
                        >
                            <i className="material-icons md-60">keyboard_arrow_right</i>
                        </div>
                        <div
                            className='lightbox-photo-leftarrow'
                            onClick={e => this.prevPhoto(e)}
                        >
                            <i className="material-icons md-60">keyboard_arrow_left</i>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

function sleep(time) {
    return new Promise(resolve => setTimeout(resolve, time));
}

PhotoLightBox.defaultProps = {
    photos: []
};
